import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import { Box, CircularProgress, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material';

import CustomBackdrop from '../../components/CustomBackdrop/CustomBackdrop';
import {
  CustomerSegmentsDocument,
  useAddSegmentCustomerMutation,
  useCustomerSegmentsQuery,
  useDeleteSegmentMutation,
} from '../../gql/generated/graphql';

import AddNewSegment from './components/AddNewSegment';
import AdminTab from './components/AdminTab';
import TabSegmentList from './components/TabSegmentList';
import TabPanel from './components/TabPanel';
import CustomerList from './components/CustomerList';
import CustomerOverview from './components/CustomerOverview';

const CustomerSegmentPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [mainTab, setMainTab] = useState(0);
  const [segmentTab, setSegmentTab] = useState(0);
  const [newSegmentData, setNewSegmentData] = useState({
    name: '',
    description: '',
    keyword1: '',
  });

  const companyParamsId = searchParams.get('companyId');

  const { data, loading } = useCustomerSegmentsQuery({
    variables: { companyId: parseInt(companyParamsId) },
    skip: !companyParamsId,
  });
  const [addSegment, { loading: addSegmentLoading }] = useAddSegmentCustomerMutation();
  const [deleteSegment, { loading: deleteSegmentLoading }] = useDeleteSegmentMutation();

  const segments = data?.customerSegments || [];
  const totalSegmentTabs = segments.length + 1; // segments + add new

  const handleMainTabChange = (_, newValue: number) => {
    setMainTab(newValue);
  };

  const handleSegmentTabChange = (_, newValue: number) => {
    setSegmentTab(newValue);
  };

  const handleNewSegmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewSegmentData({
      ...newSegmentData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSave = async () => {
    try {
      await addSegment({
        variables: {
          input: {
            bidGroupCompanyId: parseInt(companyParamsId),
            segmentName: newSegmentData.name,
            segmentDescription: newSegmentData.description,
            keyword1: newSegmentData.keyword1,
          },
        },
        refetchQueries: [
          {
            query: CustomerSegmentsDocument,
            variables: { companyId: parseInt(companyParamsId) },
          },
        ],
      });
      setNewSegmentData({ name: '', description: '', keyword1: '' });
      setSegmentTab(totalSegmentTabs - 1);
    } catch (error) {
      console.error('Error adding segment:', error);
    }
  };

  const handleDeleteSegment = (id: number) => {
    try {
      deleteSegment({
        variables: {
          segmentId: id,
        },
        refetchQueries: [
          {
            query: CustomerSegmentsDocument,
            variables: { companyId: parseInt(companyParamsId) },
          },
        ],
      });
    } catch (error) {
      console.error('Error deleting segment:', error);
    } finally {
      setSegmentTab(0);
    }
  };

  if (!companyParamsId) {
    return (
      <Typography color="error" variant="h6">
        {t('customerSegmentPage.missingCompanyId')}
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon fontSize="large" />
          Customers
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={mainTab} onChange={handleMainTabChange} aria-label="main customer tabs">
          <Tab label="Customer Overview" />
          <Tab label="Customer List" />
          <Tab label="Customer Segments" />
          <Tab label="Admin" />
        </Tabs>
      </Paper>

      {/* Customer Overview Tab */}
      <TabPanel value={mainTab} index={0}>
        <CustomerOverview companyId={parseInt(companyParamsId)} />
      </TabPanel>

      {/* Customer List Tab */}
      <TabPanel value={mainTab} index={1}>
        <CustomerList companyId={parseInt(companyParamsId)} />
      </TabPanel>

      {/* Customer Segments Tab */}
      <TabPanel value={mainTab} index={2}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <Tabs
            value={segmentTab}
            onChange={handleSegmentTabChange}
            aria-label="customer segments tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {segments.map((segment, index) => (
              <Tab key={segment.id} label={segment.segmentName} />
            ))}
            <Tooltip title={t('customerSegmentPage.addNewSegmentTooltip')}>
              <Tab icon={<AddIcon />} aria-label="add segment" />
            </Tooltip>
          </Tabs>
        </Paper>

        <TabSegmentList
          segments={segments}
          activeTab={segmentTab}
          companyId={companyParamsId}
          onDeleteSegment={handleDeleteSegment}
        />
        <AddNewSegment
          activeTab={segmentTab}
          totalTabs={totalSegmentTabs}
          newSegmentData={newSegmentData}
          handleNewSegmentChange={handleNewSegmentChange}
          handleSave={handleSave}
        />
      </TabPanel>

      {/* Admin Tab */}
      <TabPanel value={mainTab} index={3}>
        <AdminTab activeTab={0} companyId={companyParamsId} />
      </TabPanel>

      <CustomBackdrop open={addSegmentLoading || deleteSegmentLoading} />
    </Box>
  );
};

export default CustomerSegmentPage;
